import React, { useEffect, useState, useCallback, useMemo } from "react";
import { MapContainer, TileLayer, GeoJSON, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import "./CycloneMap.css";
import WeatherDetails from "../WeatherDetails/WeatherDetails";
import { useNavigate } from "react-router-dom";

const CycloneTrackingView = () => {
  const [geoJsonData, setGeoJsonData] = useState(null);
  const [cycloneData, setCycloneData] = useState([]);
  const [highlightedStates, setHighlightedStates] = useState([]);
  const [selectedCyclone, setSelectedCyclone] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [selectedYear, setSelectedYear] = useState("2024");
  const [regionFilter, setRegionFilter] = useState("all");
  const [filteredCyclones, setFilteredCyclones] = useState([]);
  const [showWeatherDetail, setShowWeatherDetail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  // Available years for selection
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 2015; year--) {
      years.push(year.toString());
    }
    return years;
  }, []);

  // Fetch GeoJSON data for the map
  useEffect(() => {
    const fetchGeoJsonData = async () => {
      try {
        const response = await fetch("/Admin2.geojson");
        if (!response.ok) throw new Error("Failed to fetch GeoJSON data");
        const data = await response.json();
        setGeoJsonData(data);
      } catch (error) {
        console.error("Error fetching GeoJSON:", error);
        setError("Failed to load map data");
      }
    };

    fetchGeoJsonData();
  }, []);

  // Fetch cyclone data when the selected year changes
  useEffect(() => {
    const fetchCycloneData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/getCycloneData?year=${selectedYear}`);
        if (!response.ok) throw new Error("Failed to fetch cyclone data");
        const data = await response.json();

        setCycloneData(data);
        setFilteredCyclones(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching cyclone data:", error);
        setError(`Failed to load cyclone data for ${selectedYear}`);
        setLoading(false);
      }
    };

    fetchCycloneData();
  }, [selectedYear]);

  // Extract affected states from cyclone data
  const extractAffectedStates = useCallback((cyclone) => {
    return Array.isArray(cyclone.areas_affected) ? cyclone.areas_affected : [];
  }, []);

  // Handle year selection change
  const handleYearChange = useCallback((event) => {
    const newYear = event.target.value;
    setSelectedYear(newYear);
    setSelectedCyclone(null);
    setHighlightedStates([]);
    setTableData(null);
  }, []);

  // Handle region filter change
  const handleRegionChange = useCallback((event) => {
    const region = event.target.value;
    setRegionFilter(region);

    if (region === "rajasthan") {
      const rajasthanCyclones = cycloneData.filter((c) =>
        c.areas_affected?.includes("Rajasthan")
      );
      setFilteredCyclones(rajasthanCyclones);
    } else {
      setFilteredCyclones(cycloneData);
    }

    setSelectedCyclone(null);
    setHighlightedStates([]);
    setTableData(null);
  }, [cycloneData]);

  // Handle cyclone selection
  const handleCycloneSelect = useCallback((event) => {
    const cycloneName = event.target.value;
    const cyclone = filteredCyclones.find((c) => c.name === cycloneName);

    if (cyclone) {
      setSelectedCyclone(cyclone);
      const affectedStates = extractAffectedStates(cyclone);
      setHighlightedStates(affectedStates);

      // Create enhanced table data
      setTableData({
        Name: cyclone.name || "N/A",
        Dates: cyclone.dates || "N/A",
        Category: cyclone.peak_intensity?.category || "N/A",
        "Wind Speed": cyclone.peak_intensity?.wind_speed
          ? `${cyclone.peak_intensity.wind_speed} km/h`
          : "N/A",
        Pressure: cyclone.peak_intensity?.pressure
          ? `${cyclone.peak_intensity.pressure} hPa`
          : "N/A",
        "Affected Areas": affectedStates.length > 0
          ? affectedStates.join(", ")
          : "None specified",
        "Impact Level": getCycloneImpactLevel(cyclone),
        "Duration": calculateCycloneDuration(cyclone.dates)
      });
    }
  }, [filteredCyclones, extractAffectedStates]);

  // Calculate cyclone impact level based on category and wind speed
  const getCycloneImpactLevel = useCallback((cyclone) => {
    const windSpeed = cyclone.peak_intensity?.wind_speed;
    const category = cyclone.peak_intensity?.category;

    if (!windSpeed && !category) return "Unknown";

    if (windSpeed) {
      if (windSpeed >= 200) return "Extremely Severe";
      if (windSpeed >= 150) return "Very Severe";
      if (windSpeed >= 100) return "Severe";
      if (windSpeed >= 60) return "Moderate";
      return "Mild";
    }

    return category || "Unknown";
  }, []);

  // Calculate cyclone duration
  const calculateCycloneDuration = useCallback((dates) => {
    if (!dates || typeof dates !== 'string') return "Unknown";

    // Simple duration calculation based on date range
    const dateRange = dates.split('-');
    if (dateRange.length === 2) {
      const startDate = new Date(dateRange[0].trim());
      const endDate = new Date(dateRange[1].trim());

      if (!isNaN(startDate) && !isNaN(endDate)) {
        const diffTime = Math.abs(endDate - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
      }
    }

    return "Unknown";
  }, []);

  // Enhanced state styling with smooth transitions
  const stateStyle = useCallback((feature) => {
    const stateName = feature.properties.ST_NM || feature.properties.state_name;
    const isHighlighted = highlightedStates.includes(stateName);

    if (isHighlighted) {
      return {
        color: "#ef4444",
        weight: 3,
        fillColor: "#f97316",
        fillOpacity: 0.7,
        dashArray: "5, 5"
      };
    }

    return {
      color: "#64748b",
      weight: 1,
      fillColor: "#e2e8f0",
      fillOpacity: 0.1
    };
  }, [highlightedStates]);

  // Enhanced feature interaction
  const onEachFeature = useCallback((feature, layer) => {
    const stateName = feature.properties.ST_NM || feature.properties.state_name;

    const affectedCyclones = filteredCyclones.filter((c) =>
      c.areas_affected?.includes(stateName)
    );

    if (affectedCyclones.length > 0) {
      const tooltipContent = affectedCyclones
        .map((c) => `<div style="margin: 4px 0;"><strong>${c.name}</strong><br/><span style="font-size: 0.9em; color: #666;">${c.dates || 'Date unknown'}</span></div>`)
        .join("");

      layer.bindTooltip(
        `<div style="padding: 8px; line-height: 1.4;">
          <h4 style="margin: 0 0 8px 0; color: #333;">${stateName}</h4>
          <div style="border-top: 1px solid #ddd; padding-top: 8px;">
            ${tooltipContent}
          </div>
        </div>`,
        {
          permanent: false,
          direction: "top",
          className: "custom-tooltip"
        }
      );
    }

    // Enhanced hover effects
    layer.on({
      mouseover: () => {
        layer.setStyle({
          weight: 3,
          color: "#3b82f6",
          fillOpacity: 0.5,
          fillColor: "#60a5fa"
        });
      },
      mouseout: () => {
        layer.setStyle(stateStyle(feature));
      },
      click: () => {
        if (affectedCyclones.length > 0) {
          // Auto-select the first cyclone affecting this state
          const firstCyclone = affectedCyclones[0];
          setSelectedCyclone(firstCyclone);
          setHighlightedStates(extractAffectedStates(firstCyclone));
          // Update table data as well
          handleCycloneSelect({ target: { value: firstCyclone.name } });
        }
      }
    });
  }, [filteredCyclones, stateStyle, extractAffectedStates, handleCycloneSelect]);

  // Loading state
  if (loading) {
    return (
      <div className="loading">
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(255,255,255,0.3)',
            borderTop: '3px solid #fff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          Loading cyclone data for {selectedYear}...
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="loading">
        <div style={{ textAlign: 'center', color: '#fca5a5' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚠️</div>
          <div>{error}</div>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              backgroundColor: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '8px',
              color: '#fff',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!cycloneData || cycloneData.length === 0) {
    return (
      <div className="loading">
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🌀</div>
          No cyclone data available for {selectedYear}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {showWeatherDetail ? (
        <WeatherDetails />
      ) : (
        <>
          {/* ── Header ── */}
          <div className="header">
            <h3>Cyclone Data Visualization</h3>
          </div>

          {/* ── Top 60%: Controls Panel + Map ── */}
          <div className="page-content">
            <div className="cyclone-controls">
              <div className="date-selector">
                <label htmlFor="year-select">Select Year:</label>
                <select
                  id="year-select"
                  onChange={handleYearChange}
                  value={selectedYear}
                  aria-label="Select year for cyclone data"
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="region-radio-buttons">
                <label>
                  <input
                    type="radio"
                    value="all"
                    checked={regionFilter === "all"}
                    onChange={handleRegionChange}
                    name="region-filter"
                    aria-label="Show all India cyclones"
                  />
                  <span>All India</span>
                </label>
                <label>
                  <input
                    type="radio"
                    value="rajasthan"
                    checked={regionFilter === "rajasthan"}
                    onChange={handleRegionChange}
                    name="region-filter"
                    aria-label="Show Rajasthan cyclones only"
                  />
                  <span>Rajasthan</span>
                </label>
              </div>

              {/* Responsive Cyclone Selector (Visible on Mobile/Tablet via CSS) */}
              <div className="cyclone-mobile-selector">
                <label htmlFor="cyclone-select-dropdown">Select Cyclone:</label>
                <select
                  id="cyclone-select-dropdown"
                  onChange={handleCycloneSelect}
                  value={selectedCyclone?.name || ""}
                  aria-label="Select cyclone from dropdown"
                >
                  <option value="" disabled>-- Choose a Cyclone --</option>
                  {filteredCyclones.map((cyclone) => (
                    <option key={cyclone.name} value={cyclone.name}>
                      {cyclone.name} {cyclone.dates ? `(${cyclone.dates})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* Desktop Cyclone Selector (Visible on Desktop via CSS) */}
              <div className="cyclone-radio-buttons">
                {filteredCyclones.length === 0 ? (
                  <div style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontStyle: 'italic',
                    textAlign: 'center',
                    padding: '1rem'
                  }}>
                    No cyclones found for the selected criteria
                  </div>
                ) : (
                  filteredCyclones.map((cyclone) => (
                    <label key={cyclone.name}>
                      <input
                        type="radio"
                        value={cyclone.name}
                        checked={selectedCyclone?.name === cyclone.name}
                        onChange={handleCycloneSelect}
                        name="cyclone-select"
                        aria-label={`Select cyclone ${cyclone.name}`}
                      />
                      <span>
                        {cyclone.name}
                        {cyclone.dates && (
                          <span style={{
                            display: 'block',
                            fontSize: '0.8rem',
                            opacity: 0.7,
                            marginTop: '2px'
                          }}>
                            {cyclone.dates}
                          </span>
                        )}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div className="right-panel">
              <MapContainer
                center={[20, 80]}
                zoom={5}
                className="map"
                scrollWheelZoom={true}
                attributionControl={true}
              >
                <TileLayer
                  url="http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {geoJsonData && (
                  <GeoJSON
                    data={geoJsonData}
                    style={stateStyle}
                    onEachFeature={onEachFeature}
                    key={`${selectedYear}-${regionFilter}-${selectedCyclone?.name}`}
                  />
                )}
              </MapContainer>
            </div>
          </div>

          {/* ── Bottom 40%: Cyclone Details Card ── */}
          {tableData && (
            <div className="table-container">
              <h5>📊 Cyclone Details</h5>
              <table className="styled-table">
                <tbody>
                  {Object.entries(tableData).map(([key, value]) => (
                    <tr key={key}>
                      <td>
                        <strong>{key}</strong>
                      </td>
                      <td>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="quick-info-banner">
                <strong>💡 Quick Info:</strong>{' '}
                Cyclone {selectedCyclone?.name} affected {highlightedStates.length} state{highlightedStates.length !== 1 ? 's' : ''}
                {selectedCyclone?.peak_intensity?.wind_speed &&
                  ` with peak winds of ${selectedCyclone.peak_intensity.wind_speed} km/h`
                }.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CycloneTrackingView;